import { Link } from "react-router-dom";

interface Props { theme: "dark" | "light"; lang: "fr" | "en" | "ar"; onLangChange: (l: "fr" | "en" | "ar") => void; onThemeToggle: () => void; }

const navLinks = {
  fr: ["Fonctionnalités", "Comment ça marche", "Télécharger", "Contact"],
  en: ["Features", "How it works", "Download", "Contact"],
  ar: ["المميزات", "كيف يعمل", "تحميل", "اتصل بنا"],
};
const navIds = ["features", "how-it-works", "download", "download"];

export default function FooterSection({ theme, lang, onLangChange, onThemeToggle }: Props) {
  const isDark = theme === "dark";
  const isRtl = lang === "ar";
  const privacyChoicesLabel =
    lang === "fr"
      ? "Choix de confidentialité"
      : lang === "en"
        ? "Privacy choices"
        : "خيارات الخصوصية";
  const accessibilityLabel =
    lang === "fr" ? "Accessibilité" : lang === "en" ? "Accessibility" : "إمكانية الوصول";
  const allLinksLabel =
    lang === "fr" ? "Tous nos liens" : lang === "en" ? "All our links" : "كل روابطنا";
  const textMuted = isDark ? "text-white/50" : "text-gray-500";
  const textLink = isDark ? "text-white/70 hover:text-white" : "text-gray-600 hover:text-gray-900";
  const borderColor = isDark ? "border-white/8" : "border-black/8";
  const links = navLinks[lang];

  const scrollTo = (id: string) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

  return (
    <footer className={`border-t ${borderColor}`} style={{ background: isDark ? "rgba(6,6,8,0.99)" : "rgba(235,235,242,0.99)" }} dir={isRtl ? "rtl" : "ltr"}>
      <div className="max-w-[1280px] mx-auto px-6 md:px-10 py-14">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-10 md:gap-12">
          {/* Brand */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <img src="https://static.readdy.ai/image/90a96f4b31fa85fb4948cb58691eee32/e03d5145bc8634f2cf637b94cf08f4b1.png" alt="Mawthoq" className="w-9 h-9 object-contain rounded-xl" />
              <span className={`text-lg font-bold ${isDark ? "text-white" : "text-gray-900"}`}>موثوق</span>
            </div>
            <p className={`text-sm mb-1 ${textMuted}`}>Built for Mauritania 🇲🇷</p>
            <p className={`text-xs leading-relaxed max-w-[220px] ${textMuted}`}>
              {lang === "fr" ? "Disponibilité carburant en temps réel pour toute la Mauritanie." : lang === "en" ? "Real-time fuel availability across all of Mauritania." : "توفر الوقود في الوقت الفعلي عبر موريتانيا."}
            </p>
            {/* The individual social handles live in the DB and are rendered on
                /links — one entry point beats three placeholder icons here. */}
            <Link
              to="/links"
              className={`inline-flex items-center gap-2 mt-5 px-3.5 py-2 rounded-full text-sm transition-all duration-200 cursor-pointer ${isDark ? "bg-white/8 border border-white/10 text-white/70 hover:text-white hover:bg-white/15" : "bg-black/8 border border-black/10 text-gray-600 hover:text-gray-900 hover:bg-black/15"}`}
            >
              <i className="ri-links-line text-base" />
              {allLinksLabel}
            </Link>
          </div>

          {/* Nav links */}
          <div>
            <h4 className={`text-xs font-semibold uppercase tracking-[0.12em] mb-5 ${textMuted}`}>{lang === "fr" ? "Navigation" : lang === "en" ? "Navigation" : "التنقل"}</h4>
            <ul className="flex flex-col gap-3">
              {links.map((link, i) => (
                <li key={link}>
                  <button onClick={() => scrollTo(navIds[i])} className={`text-sm transition-all duration-200 cursor-pointer text-left ${textLink}`}>{link}</button>
                </li>
              ))}
            </ul>
          </div>

          {/* Store badges + controls */}
          <div>
            <h4 className={`text-xs font-semibold uppercase tracking-[0.12em] mb-5 ${textMuted}`}>{lang === "fr" ? "Télécharger" : lang === "en" ? "Download" : "تحميل"}</h4>
            <div className="flex flex-col gap-3 mb-6">
              <a href="#" className={`flex items-center gap-2.5 px-4 py-2.5 rounded-xl cursor-pointer whitespace-nowrap transition-all duration-200 hover:scale-[1.02] ${isDark ? "bg-white/8 border border-white/10 text-white" : "bg-black/8 border border-black/10 text-gray-900"}`}>
                <i className="ri-apple-fill text-lg" />
                <div><div className={`text-[10px] ${textMuted}`}>{lang === "fr" ? "Télécharger sur" : "Download on the"}</div><div className="text-sm font-semibold">App Store</div></div>
              </a>
              <a href="#" className="flex items-center gap-2.5 px-4 py-2.5 rounded-xl cursor-pointer whitespace-nowrap transition-all duration-200 hover:scale-[1.02] bg-[#007AFF] text-white">
                <i className="ri-google-play-fill text-lg" />
                <div><div className="text-[10px] opacity-75">{lang === "fr" ? "Disponible sur" : "Get it on"}</div><div className="text-sm font-semibold">Google Play</div></div>
              </a>
            </div>
            {/* Theme + lang toggles */}
            <div className="flex items-center gap-2 flex-wrap">
              <div className={`flex items-center rounded-full p-0.5 text-xs font-semibold ${isDark ? "bg-white/10 border border-white/10" : "bg-black/10 border border-black/10"}`}>
                {(["fr", "en", "ar"] as const).map(l => (
                  <button key={l} onClick={() => onLangChange(l)} className={`px-2.5 py-1 rounded-full transition-all duration-200 cursor-pointer ${lang === l ? isDark ? "bg-white text-black" : "bg-black text-white" : isDark ? "text-white/60 hover:text-white" : "text-gray-500 hover:text-gray-900"}`}>
                    {l === "fr" ? "FR" : l === "en" ? "EN" : "عربي"}
                  </button>
                ))}
              </div>
              <button onClick={onThemeToggle} className={`w-8 h-8 rounded-full flex items-center justify-center cursor-pointer ${isDark ? "bg-white/10 border border-white/10 text-white" : "bg-black/10 border border-black/10 text-gray-700"}`}>
                <i className={`${isDark ? "ri-sun-line" : "ri-moon-line"} text-sm`} />
              </button>
            </div>
          </div>
        </div>

        {/* Bottom bar */}
        <div className={`mt-10 pt-8 border-t ${borderColor} flex flex-col sm:flex-row items-center justify-between gap-3`}>
          <p className={`text-xs ${textMuted} text-center sm:text-left`}>
            © 2026 Mawthoq · Mauritanie ·{" "}
            <Link
              to="/privacy-policy"
              className={`hover:underline cursor-pointer ${textMuted}`}
            >
              {lang === "fr"
                ? "Politique de confidentialité"
                : lang === "en"
                  ? "Privacy Policy"
                  : "سياسة الخصوصية"}
            </Link>
            {" · "}
            <Link
              to="/privacy-choices"
              className={`hover:underline cursor-pointer ${textMuted}`}
            >
              {privacyChoicesLabel}
            </Link>
            {" · "}
            {/* Static page (outside the SPA) referenced by the app stores. */}
            <a
              href="/delete-account"
              className={`hover:underline cursor-pointer ${textMuted}`}
            >
              {lang === "fr"
                ? "Supprimer mon compte"
                : lang === "en"
                  ? "Delete my account"
                  : "حذف الحساب"}
            </a>
            {" · "}
            <Link
              to="/accessibility"
              className={`hover:underline cursor-pointer ${textMuted}`}
            >
              {accessibilityLabel}
            </Link>
          </p>
          <div className="flex items-center gap-1.5">
            <div className="w-2 h-2 rounded-full bg-[#34C759] animate-pulse" />
            <span className={`text-xs ${textMuted}`}>{lang === "fr" ? "Tous systèmes opérationnels" : lang === "en" ? "All systems operational" : "جميع الأنظمة تعمل"}</span>
          </div>
        </div>
      </div>
    </footer>
  );
}
